import { type Retention, beyondRetention, isBeyond, retentionBand } from "./retention.js";

/** Полоса записи, как её отдаёт `/api/archive/{camera}/timeline`. */
export interface SpanIn {
  start: string;
  end: string;
  /** local | s3 | both — где лежат байты. */
  tier: string;
  segments: number;
}

export interface Span {
  start: number;
  end: number;
  tier: string;
}

const RECORDED = "#3f8f4f";
const COLOURS: Record<string, string> = {
  both: RECORDED,
  local: "#4f8cff",
  s3: "#7a6cc4",
};
const GAP = "#5c2b2b";
/** За границей срока хранения: записи нет по плану, а не потому, что камера не писала. */
const EXPIRED = "#4a4d55";
const FUTURE = "#262a33";
const PLAYHEAD = "#ffd24a";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/** Самое сильное приближение: пять минут на всю ширину. */
const MIN_VIEW_MS = 5 * MINUTE;

/** Шаги меток; берётся первый, при котором метки не налезают друг на друга. */
const STEPS = [
  MINUTE,
  5 * MINUTE,
  10 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  HOUR,
  2 * HOUR,
  3 * HOUR,
];

/** Сколько пикселей сдвига отличают перетаскивание от щелчка. */
const DRAG_THRESHOLD_PX = 4;

export function parseSpans(spans: SpanIn[]): Span[] {
  return spans.map((s) => ({
    start: Date.parse(s.start),
    end: Date.parse(s.end),
    tier: s.tier,
  }));
}

/** «14:32:07» по местному времени. */
export function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString("ru-RU", { hour12: false });
}

/** «14 окт 14:32:07». */
export function stamp(ms: number): string {
  const day = new Date(ms).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
  return `${day} ${clock(ms)}`;
}

/**
 * Полоса одних суток.
 *
 * **Разрывы рисуются красным, и это главное, что здесь есть.** На антикражной
 * системе «когда камера не писала» — вопрос, который зададут обязательно,
 * возможно страховщик. Поэтому красным закрашивается всё, что не закрыто
 * записью, — кроме будущего: то, что ещё не наступило, дырой не является и
 * рисуется отдельно, иначе сегодняшние сутки выглядели бы наполовину
 * пропавшими.
 *
 * Время всюду местное: оператор ищет «в половине третьего ночи», а не
 * «в 23:30 UTC».
 */
export class Timeline {
  private spans: Span[] = [];
  private retention: Retention | null = null;
  private dayStart = 0;
  private dayEnd = 0;
  private viewFrom = 0;
  private viewTo = 0;
  private playhead: number | null = null;
  private hoverX: number | null = null;
  private drag: { x: number; from: number; to: number; moved: boolean } | null = null;

  /** Щелчок по полосе: какой момент выбран. */
  onPick: (ms: number) => void = () => {};

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly overview: HTMLCanvasElement,
    private readonly tip: HTMLElement,
  ) {
    canvas.addEventListener("pointerdown", (e) => this.pointerDown(e));
    canvas.addEventListener("pointermove", (e) => this.pointerMove(e));
    canvas.addEventListener("pointerup", (e) => this.pointerUp(e));
    canvas.addEventListener("pointerleave", () => {
      if (this.drag) return;
      this.hoverX = null;
      this.tip.hidden = true;
      this.draw();
    });
    canvas.addEventListener("wheel", (e) => this.wheel(e), { passive: false });
    overview.addEventListener("click", (e) => this.overviewClick(e));
    window.addEventListener("resize", () => this.draw());
  }

  /** Новые сутки: приближение сбрасывается на сутки целиком. */
  setDay(dayStart: number, dayEnd: number, spans: Span[]): void {
    this.dayStart = dayStart;
    this.dayEnd = dayEnd;
    this.viewFrom = dayStart;
    this.viewTo = dayEnd;
    this.spans = spans;
    this.draw();
  }

  /** Граница срока хранения; `null` — срока нет. */
  setRetention(retention: Retention | null): void {
    this.retention = retention;
    this.draw();
  }

  /** Лежит ли момент за границей срока хранения. */
  beyondRetention(ms: number): boolean {
    return isBeyond(ms, this.retention);
  }

  /** Те же сутки, свежие полосы: приближение остаётся. */
  setSpans(spans: Span[]): void {
    this.spans = spans;
    this.draw();
  }

  setPlayhead(ms: number | null): void {
    this.playhead = ms;
    // Бегунок ушёл за край приближения — едем за ним, иначе человек теряет
    // кадр из виду ровно тогда, когда смотрит запись.
    if (ms !== null && this.zoomed && (ms < this.viewFrom || ms > this.viewTo)) {
      const width = this.viewTo - this.viewFrom;
      this.setView(ms - width * 0.1, ms + width * 0.9);
      return;
    }
    this.draw();
  }

  get zoomed(): boolean {
    return this.viewTo - this.viewFrom < this.dayEnd - this.dayStart;
  }

  resetZoom(): void {
    this.setView(this.dayStart, this.dayEnd);
  }

  spanAt(ms: number): Span | null {
    return this.spans.find((s) => ms >= s.start && ms < s.end) ?? null;
  }

  /** Ближайшая запись после момента, в пределах суток. */
  nextSpanAfter(ms: number): Span | null {
    return this.spans.find((s) => s.start > ms) ?? null;
  }

  // --- ввод ---------------------------------------------------------------

  private pointerDown(e: PointerEvent): void {
    this.canvas.setPointerCapture(e.pointerId);
    this.drag = { x: e.clientX, from: this.viewFrom, to: this.viewTo, moved: false };
  }

  private pointerMove(e: PointerEvent): void {
    const drag = this.drag;
    if (drag) {
      const dx = e.clientX - drag.x;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) drag.moved = true;
      if (drag.moved && this.zoomed) {
        const msPerPx = (drag.to - drag.from) / this.canvas.clientWidth;
        this.setView(drag.from - dx * msPerPx, drag.to - dx * msPerPx);
      }
    }
    this.hoverX = this.localX(e);
    this.showTip();
    this.draw();
  }

  private pointerUp(e: PointerEvent): void {
    const drag = this.drag;
    this.drag = null;
    if (!drag || drag.moved) return;
    const ms = this.timeAt(this.localX(e));
    if (ms < this.dayStart || ms >= this.dayEnd) return;
    this.onPick(ms);
  }

  private wheel(e: WheelEvent): void {
    e.preventDefault();
    const anchor = this.timeAt(this.localX(e));
    const factor = Math.exp(e.deltaY * 0.0015);
    const span = Math.max(
      MIN_VIEW_MS,
      Math.min(this.dayEnd - this.dayStart, (this.viewTo - this.viewFrom) * factor),
    );
    // Момент под курсором остаётся под курсором.
    const share = (anchor - this.viewFrom) / (this.viewTo - this.viewFrom);
    this.setView(anchor - span * share, anchor + span * (1 - share));
    this.showTip();
  }

  private overviewClick(e: MouseEvent): void {
    const rect = this.overview.getBoundingClientRect();
    const ms =
      this.dayStart + ((e.clientX - rect.left) / rect.width) * (this.dayEnd - this.dayStart);
    const width = this.viewTo - this.viewFrom;
    this.setView(ms - width / 2, ms + width / 2);
  }

  private setView(from: number, to: number): void {
    const width = Math.min(to - from, this.dayEnd - this.dayStart);
    let start = Math.max(this.dayStart, from);
    if (start + width > this.dayEnd) start = this.dayEnd - width;
    this.viewFrom = start;
    this.viewTo = start + width;
    this.draw();
  }

  private localX(e: MouseEvent): number {
    return e.clientX - this.canvas.getBoundingClientRect().left;
  }

  private timeAt(x: number): number {
    return this.viewFrom + (x / this.canvas.clientWidth) * (this.viewTo - this.viewFrom);
  }

  private xOf(ms: number, width: number, from: number, to: number): number {
    return ((ms - from) / (to - from)) * width;
  }

  /** Подсказка под курсором: точное время и что в этот момент есть. */
  private showTip(): void {
    if (this.hoverX === null) return;
    const ms = this.timeAt(this.hoverX);
    let what: string;
    if (ms > Date.now()) what = "ещё не наступило";
    else if (this.spanAt(ms)) what = "есть запись";
    else if (this.retention && isBeyond(ms, this.retention)) what = beyondRetention(this.retention.days);
    else what = "записи нет";
    this.tip.textContent = `${clock(ms)} · ${what}`;
    this.tip.hidden = false;
    const max = this.canvas.clientWidth - this.tip.offsetWidth;
    this.tip.style.left = `${Math.max(0, Math.min(max, this.hoverX - this.tip.offsetWidth / 2))}px`;
  }

  // --- рисование ----------------------------------------------------------

  private draw(): void {
    if (this.dayEnd <= this.dayStart) return;
    this.paint(this.canvas, this.viewFrom, this.viewTo, true);
    this.paint(this.overview, this.dayStart, this.dayEnd, false);
  }

  private paint(canvas: HTMLCanvasElement, from: number, to: number, main: boolean): void {
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (width === 0) return;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const x = (ms: number) => this.xOf(ms, width, from, to);

    // Сначала весь интервал красным: то, что не закрашено записью, и есть
    // дыра. Так дыру нельзя забыть нарисовать.
    ctx.fillStyle = GAP;
    ctx.fillRect(0, 0, width, height);

    // Поверх красного — серым то, что за границей срока хранения: там записи
    // нет по плану, и путать это с дырой нельзя.
    const band = retentionBand(from, to, this.retention);
    if (band) {
      ctx.fillStyle = EXPIRED;
      ctx.fillRect(0, 0, x(band.end), height);
    }

    for (const span of this.spans) {
      if (span.end <= from || span.start >= to) continue;
      const left = x(Math.max(span.start, from));
      const right = x(Math.min(span.end, to));
      ctx.fillStyle = COLOURS[span.tier] ?? RECORDED;
      ctx.fillRect(left, 0, Math.max(1, right - left), height);
    }

    // Будущее — не дыра. Серым со штриховкой и линия «сейчас».
    const now = Date.now();
    if (now < to) {
      const left = Math.max(0, x(now));
      ctx.fillStyle = FUTURE;
      ctx.fillRect(left, 0, width - left, height);
      ctx.save();
      ctx.beginPath();
      ctx.rect(left, 0, width - left, height);
      ctx.clip();
      ctx.strokeStyle = "rgba(255,255,255,0.07)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let hx = left - height; hx < width; hx += 8) {
        ctx.moveTo(hx, height);
        ctx.lineTo(hx + height, 0);
      }
      ctx.stroke();
      ctx.restore();
      if (now > from) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(left - 1, 0, 2, height);
      }
    }

    if (main) {
      this.paintTicks(ctx, width, height, from, to);
      if (this.hoverX !== null) {
        ctx.fillStyle = "rgba(255,255,255,0.8)";
        ctx.fillRect(Math.round(this.hoverX), 0, 1, height);
      }
    } else if (this.zoomed) {
      // Рамка видимого окна на обзорной полосе.
      const left = x(this.viewFrom);
      const right = x(this.viewTo);
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      ctx.fillRect(left, 0, Math.max(2, right - left), height);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1;
      ctx.strokeRect(left + 0.5, 0.5, Math.max(2, right - left) - 1, height - 1);
    }

    if (this.playhead !== null && this.playhead >= from && this.playhead <= to) {
      const px = x(this.playhead);
      ctx.fillStyle = PLAYHEAD;
      ctx.fillRect(px - 1, 0, 2, height);
      if (main) {
        ctx.beginPath();
        ctx.moveTo(px - 5, 0);
        ctx.lineTo(px + 5, 0);
        ctx.lineTo(px, 6);
        ctx.fill();
      }
    }
  }

  private paintTicks(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    from: number,
    to: number,
  ): void {
    const msPerPx = (to - from) / width;
    const step = STEPS.find((s) => s / msPerPx >= 64) ?? 3 * HOUR;
    ctx.font = "11px system-ui, sans-serif";
    // Метки от местной полуночи, а не от эпохи: иначе при часовом поясе с
    // получасом метки «каждый час» стояли бы на половинах.
    const first = this.dayStart + Math.ceil((from - this.dayStart) / step) * step;
    for (let t = first; t < to; t += step) {
      const px = this.xOf(t, width, from, to);
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.fillRect(px, height - 8, 1, 8);
      const d = new Date(t);
      const label = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.fillText(label, px + 3, height - 6);
    }
  }
}
